import React, { useCallback, useMemo, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconButton from "../../components/common/IconButton";
import { MdDelete } from "react-icons/md";
import {
  requiredArrayValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import { DAYS_OF_WEEK } from "../../_contstants/common";
import FormTimePicker from "../../components/formFields/FormTimeField";
import moment from "moment";
import { FaPlus } from "react-icons/fa";

const TimingRow = React.memo(
  ({
    index,
    fieldArrayRenderProps,
    handleOpenDeleteDialog,
    pushElementInarray,
  }: {
    index: number;
    fieldArrayRenderProps: any;
    handleOpenDeleteDialog: (index: number) => void;
    pushElementInarray: () => void;
  }) => {
    const startTimeValue = fieldArrayRenderProps.value?.[index]?.StartTime;
    const endTimeValue = fieldArrayRenderProps.value?.[index]?.EndTime;

    const handleStartTimeChange = useCallback(
      (e: any) => {
        // Update StartTime itself
        fieldArrayRenderProps.formRenderProps.onChange(
          `Timings.${index}.StartTime`,
          {
            value: e.value,
          },
        );

        // Sync EndTime if it's a new entry
        if (!fieldArrayRenderProps.value[index]?.ID && e && e?.value) {
          fieldArrayRenderProps.formRenderProps.onChange(
            `Timings.${index}.EndTime`,
            {
              value: e.value,
            },
          );
        }
      },
      [
        index,
        fieldArrayRenderProps.formRenderProps,
        fieldArrayRenderProps.value,
      ],
    );

    const startTimeValidator = useCallback(
      (value: any) => {
        const err = requiredValidator(value);
        if (err) return err;
        if (endTimeValue && moment(value).isAfter(moment(endTimeValue))) {
          return "Start time cannot be after end time";
        }
        return "";
      },
      [endTimeValue],
    );

    const endTimeValidator = useCallback(
      (value: any) => {
        const err = requiredValidator(value);
        if (err) return err;
        if (startTimeValue && moment(value).isBefore(moment(startTimeValue))) {
          return "End time cannot be before start time";
        }
        return "";
      },
      [startTimeValue],
    );

    const minEndTime = useMemo(() => {
      return startTimeValue ? moment(startTimeValue).toDate() : undefined;
    }, [startTimeValue]);

    return (
      <GridLayout
        style={{ marginRight: 30 }}
        gap={{ rows: 10, cols: 10 }}
        cols={[
          { width: "30%" },
          { width: "30%" },
          { width: "30%" },
          { width: "10%" },
        ]}
      >
        <GridLayoutItem>
          <Field
            wrapperClassName="m-0"
            name={`Timings.${index}.Day`}
            label="Day"
            placeholder="Select day"
            component={FormMultiSelectionFiled}
            options={DAYS_OF_WEEK}
            validator={requiredArrayValidator}
            astrike={true}
          />
        </GridLayoutItem>
        <GridLayoutItem>
          <Field
            wrapperClassName="m-0"
            name={`Timings.${index}.StartTime`}
            label="Start Time"
            component={FormTimePicker}
            validator={startTimeValidator}
            onChange={handleStartTimeChange}
            astrike={true}
          />
        </GridLayoutItem>
        <GridLayoutItem>
          <Field
            wrapperClassName="m-0"
            name={`Timings.${index}.EndTime`}
            label="End Time"
            component={FormTimePicker}
            min={minEndTime}
            validator={endTimeValidator}
            astrike={true}
          />
        </GridLayoutItem>
        <GridLayoutItem className="d-flex justify-content-end gap-2">
          {fieldArrayRenderProps?.value?.length === index + 1 && (
            <IconButton
              onClick={() => pushElementInarray()}
              themeColor={"primary"}
              size={"small"}
              fillMode={"solid"}
              type="button"
              style={{
                marginTop: 20,
                height: 30,
                width: 30,
              }}
              icon={FaPlus}
              iconClassName="absolute-position"
              iconStyle={{ fontSize: "20px" }}
            />
          )}
          <IconButton
            onClick={() => handleOpenDeleteDialog(index)}
            disabled={fieldArrayRenderProps?.value?.length === 1}
            themeColor={"error"}
            size={"small"}
            fillMode={"solid"}
            type="button"
            style={{
              marginTop: 20,
              height: 30,
              width: 30,
            }}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        </GridLayoutItem>
      </GridLayout>
    );
  },
);

const DoctorTimingArray = (fieldArrayRenderProps: FieldArrayRenderProps) => {
  const dispatch = useAppDispatch();
  const [deleteIndex, setDeleteIndex] = useState(-1);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleOpenDeleteDialog = useCallback(
    (index: number) => {
      dispatch(openDialog("deleteDoctorTime"));
      setDeleteIndex(index);
    },
    [dispatch],
  );

  const handleDeleteAction = useCallback(
    (index: number) => {
      fieldArrayRenderProps.onRemove({ index: index });
      dispatch(closeDialog());
      setDeleteIndex(-1);
    },
    [fieldArrayRenderProps, dispatch],
  );

  const pushElementInarray = useCallback(() => {
    fieldArrayRenderProps.onPush({
      value: {
        Day: [],
        StartTime: null,
        EndTime: null,
      },
    });
  }, [fieldArrayRenderProps.onPush]);

  React.useEffect(() => {
    if (!fieldArrayRenderProps?.value?.length) {
      pushElementInarray();
    }
  }, [fieldArrayRenderProps?.value?.length, pushElementInarray]);

  return (
    <>
      <GridLayoutItem
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Typography.h5>Add Doctor Timing</Typography.h5>
        {fieldArrayRenderProps.value &&
          fieldArrayRenderProps.value.length < 1 && (
            <div>
              <Button
                fillMode={"solid"}
                themeColor={"primary"}
                onClick={pushElementInarray}
                type="button"
              >
                Add New
              </Button>
            </div>
          )}
      </GridLayoutItem>

      {dialogName === "deleteDoctorTime" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Doctor Timing"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Doctor Timing?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(deleteIndex)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
      <GridLayoutItem
        style={{ display: "flex", flexDirection: "column", gap: "20px" }}
      >
        {fieldArrayRenderProps.value &&
          fieldArrayRenderProps.value.length > 0 &&
          fieldArrayRenderProps.value?.map((Timings: any, index: number) => (
            <TimingRow
              key={index}
              index={index}
              fieldArrayRenderProps={fieldArrayRenderProps}
              handleOpenDeleteDialog={handleOpenDeleteDialog}
              pushElementInarray={pushElementInarray}
            />
          ))}
      </GridLayoutItem>
    </>
  );
};

export default DoctorTimingArray;
